import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, LinearProgress, Paper } from '@mui/material';
import { useTheme } from '@mui/material';
import { tokens } from '../../theme';
import Axios from 'axios';
import { getAuthHeader } from '../../utils/authToken';

const ExtractionJobMonitor = ({ jobId, onComplete, onError }) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    const [status, setStatus] = useState('pending');
    const [progress, setProgress] = useState(0);
    const [statusMessage, setStatusMessage] = useState('Initializing...');
    const pollingIntervalRef = useRef(null);
    const mountedRef = useRef(true);

    useEffect(() => {
        mountedRef.current = true;

        // Start polling
        pollJobStatus();
        pollingIntervalRef.current = setInterval(pollJobStatus, 3000); // Poll every 3 seconds

        // Cleanup
        return () => {
            mountedRef.current = false;
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
            }
        };
    }, [jobId]);

    const pollJobStatus = async () => {
        try {
            const response = await Axios.get(
                `${process.env.REACT_APP_URL}/api/v1/hw-questions/extraction-jobs/${jobId}`,
                {
                    headers: {
                        Authorization: getAuthHeader(),
                    },
                }
            );

            if (!mountedRef.current) return;

            const data = response.data;
            setStatus(data.status);
            setProgress(data.progress || 0);

            // Update status message based on progress
            if (data.status === 'pending') {
                setStatusMessage('Waiting to start...');
            } else if (data.status === 'processing') {
                if (data.progress < 20) {
                    setStatusMessage('Initializing extraction...');
                } else if (data.progress < 50) {
                    setStatusMessage('Performing OCR with Mathpix...');
                } else if (data.progress < 90) {
                    setStatusMessage('Analyzing questions with AI...');
                } else {
                    setStatusMessage('Finalizing...');
                }
            } else if (data.status === 'completed') {
                setStatusMessage('Extraction completed!');
                if (pollingIntervalRef.current) {
                    clearInterval(pollingIntervalRef.current);
                }
                // Call onComplete with extracted questions
                if (data.extracted_questions && data.extracted_questions.length > 0) {
                    onComplete(data.extracted_questions);
                } else {
                    onError('No questions were extracted');
                }
            } else if (data.status === 'failed') {
                setStatusMessage('Extraction failed');
                if (pollingIntervalRef.current) {
                    clearInterval(pollingIntervalRef.current);
                }
                onError(data.error_message || 'Extraction failed');
            }
        } catch (error) {
            console.error('Polling error:', error);
            if (mountedRef.current) {
                if (pollingIntervalRef.current) {
                    clearInterval(pollingIntervalRef.current);
                }
                onError(error.response?.data?.error || 'Failed to check extraction status');
            }
        }
    };

    return (
        <Box>
            <Paper
                elevation={3}
                sx={{
                    padding: 4,
                    maxWidth: 600,
                    margin: '0 auto',
                    textAlign: 'center',
                }}
            >
                <Typography variant="h5" fontWeight="bold" mb={3}>
                    Extracting Questions...
                </Typography>

                <Box mb={3}>
                    <Typography variant="body1" color="textSecondary" mb={2}>
                        {statusMessage}
                    </Typography>
                    <LinearProgress
                        variant="determinate"
                        value={progress}
                        sx={{
                            height: 10,
                            borderRadius: 5,
                            backgroundColor: colors.primary[700],
                            '& .MuiLinearProgress-bar': {
                                backgroundColor: colors.greenAccent[500],
                            },
                        }}
                    />
                    <Typography variant="body2" color="textSecondary" mt={1}>
                        {progress}%
                    </Typography>
                </Box>

                <Box>
                    <Typography variant="body2" color="textSecondary">
                        Status: <strong>{status}</strong>
                    </Typography>
                    <Typography variant="caption" color="textSecondary" display="block" mt={1}>
                        This may take a few minutes depending on the number of pages and complexity of questions.
                    </Typography>
                </Box>
            </Paper>
        </Box>
    );
};

export default ExtractionJobMonitor;
